import type { Router } from 'vue-router';
import { useLegalStore } from '../stores/legalStore';

// List of public routes that don't require consent checking
const PUBLIC_ROUTES = ['/', '/login', '/auth/callback', '/legal', '/register', '/about', '/reconsent-gate'];

// List of protected routes that require consent checking
const PROTECTED_ROUTES = ['/projects', '/profile', '/consents', '/reconsent', '/dashboard', '/settings', '/app'];

// List of documents that require consent
const RECONSENT_REQUIRED_FOR = ['tos', 'privacy'];

// Route guard to check user consent status
export const consentGuard = async (to: any, _from: any, next: any) => {
    // Check if the route is public
    const isPublicRoute = PUBLIC_ROUTES.some(route =>
        to.path === route || to.path.startsWith(route + '/') || to.path.startsWith(route + '?')
    );

    // Special check for legal documents - they are public
    const isLegalDocumentRoute = to.path.startsWith('/legal/');

    // If route is public or a legal document, skip consent checking
    if (isPublicRoute || isLegalDocumentRoute) {
        return next();
    }

    // Check if the route is protected
    const isProtectedRoute = PROTECTED_ROUTES.some(route => to.path.startsWith(route));

    // If route is neither protected nor public, treat it as public by default
    if (!isProtectedRoute) {
        return next();
    }

    try {
        // Check consent status for each required document
        const legalStore = useLegalStore();
        let needsReconsent = false;

        for (const slug of RECONSENT_REQUIRED_FOR) {
            const consentStatus = await legalStore.getUserConsentStatus(slug);
            if (!consentStatus.hasConsented) {
                needsReconsent = true;
                break;
            }
        }

        if (needsReconsent) {
            // Save the path user came from to return them there later
            localStorage.setItem('consentRedirectPath', to.fullPath);

            // Redirect to reconsent gate screen
            next('/reconsent-gate');
        } else {
            // If consent exists, continue navigation
            next();
        }
    } catch (error) {
        console.error('Error checking consent status:', error);
        // In case of error, skip checking and continue navigation
        next();
    }
};

// Function to register route guard
export const setupConsentGuard = (router: Router) => {
    router.beforeEach(consentGuard);
};